import React, { useEffect, useCallback, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

const RAPIDAPI_KEY = '4c66b9d960msh02b8d0e87b75da6p1592ebjsnb75d982894ba';

// Global translation cache
const pageTranslationCache = {};

// Translate a single text
const translateSingleText = async (text, targetLang) => {
  const cacheKey = `${targetLang}:${text}`;
  if (pageTranslationCache[cacheKey]) {
    return pageTranslationCache[cacheKey];
  }

  try {
    const response = await fetch(
      `https://free-google-translator.p.rapidapi.com/external-api/free-google-translator?from=en&to=${targetLang}&query=${encodeURIComponent(text)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-rapidapi-host': 'free-google-translator.p.rapidapi.com',
          'x-rapidapi-key': RAPIDAPI_KEY
        },
        body: JSON.stringify({ translate: 'rapidapi' })
      }
    );

    const data = await response.json();
    if (data.translation) {
      pageTranslationCache[cacheKey] = data.translation;
      return data.translation;
    }
    return text;
  } catch (error) {
    console.error('Translation error:', error);
    return text;
  }
};

// Component that auto-translates all text content in its children
const PageTranslator = ({ children }) => {
  const { currentLanguage } = useLanguage();
  const [isTranslating, setIsTranslating] = useState(false);

  const translatePage = useCallback(async () => {
    if (currentLanguage === 'en') {
      // Reset to original text
      document.querySelectorAll('[data-original-text]').forEach(el => {
        el.textContent = el.getAttribute('data-original-text');
      });
      return;
    }

    setIsTranslating(true);

    // Find all text nodes that should be translated
    const textElements = document.querySelectorAll(
      'h1, h2, h3, h4, h5, h6, p, span, button, label, a, th, td, li'
    );

    const elementsToTranslate = [];

    textElements.forEach(el => {
      // Skip elements with no direct text or that are input/script elements
      if (!el.childNodes.length) return;
      if (el.closest('script, style, input, textarea, select')) return;
      if (el.closest('[data-no-translate]')) return;

      // Get direct text content (not from children)
      const directText = Array.from(el.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE)
        .map(node => node.textContent.trim())
        .join(' ')
        .trim();

      if (directText && directText.length > 1 && !/^[\d\s\.\,\%\$\=\+\-\*\/\(\)]+$/.test(directText)) {
        // Store original text
        if (!el.hasAttribute('data-original-text')) {
          el.setAttribute('data-original-text', directText);
        }
        elementsToTranslate.push({ el, text: el.getAttribute('data-original-text') });
      }
    });

    // Translate in batches with delay to avoid rate limiting
    for (let i = 0; i < elementsToTranslate.length; i++) {
      const { el, text } = elementsToTranslate[i];
      try {
        const translated = await translateSingleText(text, currentLanguage);
        
        // Replace only the text content, preserving child elements
        el.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            node.textContent = translated;
          }
        });
      } catch (error) {
        console.error('Failed to translate:', text, error);
      }

      // Small delay between translations to avoid rate limiting
      if (i < elementsToTranslate.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    setIsTranslating(false);
  }, [currentLanguage]);

  useEffect(() => {
    // Delay translation to ensure DOM is ready
    const timer = setTimeout(() => {
      translatePage();
    }, 500);

    return () => clearTimeout(timer);
  }, [translatePage, currentLanguage]);

  return (
    <>
      {isTranslating && (
        <div className="fixed top-16 right-4 z-50 bg-cyan-600 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 shadow-lg">
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Translating...
        </div>
      )}
      {children}
    </>
  );
};

export default PageTranslator;
